import {CastorService} from '@aipb/gulliver/castor/data-access';
import {
  AutocompleteFilters,
  AutocompleteResult,
  CASTOR_FIELD_TYPE_TEXT,
  CoreFieldInfoLang,
  SortType
} from '@aipb/gulliver/castor/model';
import {HighlightPipe} from '@aipb/shared/ui/pipes';

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  signal,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormControl
} from '@angular/forms';
import {
  MAT_AUTOCOMPLETE_SCROLL_STRATEGY_FACTORY_PROVIDER,
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger
} from '@angular/material/autocomplete';
import {MatButtonModule} from '@angular/material/button';
import {MatOptionSelectionChange} from '@angular/material/core';
import {FloatLabelType, MatFormFieldModule} from '@angular/material/form-field';
import {MatInput, MatInputModule} from '@angular/material/input';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {TranslocoModule} from '@jsverse/transloco';
import {Observable, of, tap} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, finalize, map, switchMap} from 'rxjs/operators';
import {MatIconModule} from '@angular/material/icon';
import {
  VirtualKeyboard,
  VirtualKeyboardDialogComponent,
  VirtualKeyboardDialogDataModel,
  VirtualKeyboardDialogService
} from '@aipb/gulliver/virtual-keyboard';
import {SearchKeyboardService} from '@aipb/gulliver/catalog/feature-search-form';
import {uniqueId} from '@aipb/shared/util/other';
import {CommonModule} from '@angular/common';
import {DialogRef} from "@angular/cdk/dialog";

@Component({
  selector: 'castor-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    HighlightPipe,
    MatIconModule
  ],
  providers: [
    SearchKeyboardService,
    MAT_AUTOCOMPLETE_SCROLL_STRATEGY_FACTORY_PROVIDER,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteComponent),
      multi: true
    }
  ]
})
export class AutocompleteComponent implements ControlValueAccessor, OnChanges, OnDestroy {
  public control: UntypedFormControl;
  public options: AutocompleteResult[] = [];
  public isLoading = false;
  public activeWord = '';
  identifier = uniqueId();
  @Input() floatLabel: FloatLabelType = 'auto';
  @Input() field: CoreFieldInfoLang;
  @Input() lang: string;
  @Input() offset: number;
  @Input() limit: number;
  @Input() filters: AutocompleteFilters;
  @Input() sort: SortType = 'index';
  @Input() query: string;
  @Input() coreId: string;
  @Input() serverId: string;
  @Input() label = 'Autocomplete';
  @Input() showNumOfResults = true;
  @Input() hint: string;
  @Input() ascii = false;
  @Input() multipleWordsJoin = true;
  @Input() multipleWordsSeparator = 'AND';
  @Input() logOperator = 'AND';
  @Input() virtualKeyboard: VirtualKeyboard;

  @Output() optionSelected: EventEmitter<MatAutocompleteSelectedEvent> = new EventEmitter();
  @Output() keydownEnter: EventEmitter<MatAutocompleteSelectedEvent> = new EventEmitter();
  @ViewChild(MatInput) input: MatInput;
  @ViewChild(MatAutocompleteTrigger, {read: MatAutocompleteTrigger})
  autocompleteTrigger: MatAutocompleteTrigger;
  keyboardOpen = signal(false);
  // eslint-disable-next-line @typescript-eslint/member-ordering
  private _lastInputValue: string;
  // eslint-disable-next-line @typescript-eslint/member-ordering
  private _lastCursorPosition = 0;
  private _virtualKeyboardRef: DialogRef<VirtualKeyboardDialogDataModel, VirtualKeyboardDialogComponent>;
  private virtualKeyboardDialog = inject(VirtualKeyboardDialogService);

  constructor(private castor: CastorService, private cdr: ChangeDetectorRef) {
    this.control = new UntypedFormControl();
    this.control.valueChanges
      .pipe(
        tap((value) => {
          this.onChange(value);

          if (this._virtualKeyboardRef) {
            this._virtualKeyboardRef.componentRef.instance.setValue(value);
          }
        }),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((value) => {
          this.isLoading = true;

          return this.processAutocompleteValue(value).pipe(
            catchError(() => {
              return of([]);
            }),
            finalize(() => {
              this.isLoading = false;
              this.cdr.detectChanges();
            })
          );
        })
      )
      .subscribe((data) => {
        this.options = data;
        this.cdr.detectChanges();
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onTouched: () => void = () => {
  };

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange = (value: string) => {
  };

  clear(): void {
    this.control.setValue('');
    if (this._virtualKeyboardRef) {
      this._virtualKeyboardRef.componentRef.instance.clearInput();
    }
  }

  showClearButton(): boolean {
    if (typeof this.control.value === 'object') {
      return this.control.value && Object.keys(this.control.value)[0].length > 0;
    }
    return this.control.value && this.control.value.length > 0;
  }

  getLabel(option: AutocompleteResult): string {
    return Object.keys(option)[0];
  }

  getNumOfResults(option: AutocompleteResult): number {
    return +option[Object.keys(option)[0]];
  }

  getWords(value: string): string[] {
    // eslint-disable-next-line no-useless-escape
    return value.split(/[\s-\/]/);
  }

  getActiveWordIndex(words: string[]): number {
    let len = 0;
    let activeWordIndex = -1;

    for (let i = 0; i < words.length; i++) {
      len += words[i].length;

      if (this._lastCursorPosition <= len) {
        activeWordIndex = i;
        break;
      }

      len++;
    }

    return activeWordIndex;
  }

  writeValue(val: any): void {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    if (!val) return;

    if (typeof val === 'object') {
      this.control.setValue(val);
      return;
    }

    this.control.setValue(val);
  }

  registerOnChange(onChange: any): void {
    this.onChange = onChange;
    //this.control.valueChanges.subscribe(onChange);
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.control.disable() : this.control.enable();
  }

  displayFn(value?: { [key: string]: string } | string): string | undefined {
    if (!value) {
      return undefined;
    }

    if (typeof value === 'object') {
      return Object.keys(value)[0];
    }
    return value;
  }

  onKeyDownEnter(evt): void {
    this.autocompleteTrigger.closePanel();
    this.keydownEnter.emit(evt);
  }

  onKeyDownArrowRight(evt): void {
    const activeOption = this.autocompleteTrigger.activeOption;

    if (activeOption) {
      this.control.setValue(activeOption.value);
    }
  }

  onOptionSelected(evt: MatAutocompleteSelectedEvent): void {
    /*if (this.field && this.field.castorType === CASTOR_FIELD_TYPE_TEXT) {
      const value = this._lastInputValue;

      const words = this.getWords(value);

      const activeWordIndex = this.getActiveWordIndex(words);

      words.splice(activeWordIndex, 1, Object.keys(evt.option.value)[0]);

      this.control.setValue({ [words.join(' ')]: 0 });
    }*/

    this.optionSelected.emit(evt);
  }

  onSelectionChange(evt: MatOptionSelectionChange): void {
    this._lastInputValue =
      typeof this.control.value === 'object' ? Object.keys(this.control.value)[0] : this.control.value;
  }

  autocomplete(value: string, query?: string) {
    return this.castor
      .autocomplete({
        facet: {
          field: this.field ? this.field.fieldId : undefined,
          prefix: value ? value.toLowerCase() : undefined,
          lang: this.lang || undefined,
          offset: this.offset || undefined,
          limit: this.limit || undefined,
          sort: this.sort || undefined,
          ascii: this.ascii
        },
        filters: {
          facetAscii: this.filters ? this.filters.facetAscii || undefined : undefined,
          asciiSearch: this.filters ? this.filters.asciiSearch || undefined : undefined,
          stopwords: this.filters ? this.filters.stopwords || undefined : undefined,
          synonyms: this.filters ? this.filters.synonyms || undefined : undefined
        },
        query: query || this.query || undefined,
        coreId: this.coreId || null
      })
      .pipe(
        map((data) => {
          return data;
        })
      );
  }

  ngOnChanges(changes: SimpleChanges): void {
    //Called before any other lifecycle hook. Use it to inject dependencies, but avoid any serious work here.
    //Add '${implements OnChanges}' to the class.
    if (changes['field'] && changes['field'].previousValue?.fieldId !== changes['field'].currentValue?.fieldId) {
      let val = this.control.value;

      if (val === null) {
        return;
      }

      if (typeof val === 'object') {
        val = Object.keys(val)[0];
      }

      if (!this.field || val.length === 0) {
        this.options = [];
        return;
      }

      this.autocomplete(val).subscribe((data) => {
        this.options = data;
      });
    }
  }

  onFocus(evt) {
    /*this.autocomplete(evt.target.value).subscribe(data => {
      this.options = data.response.result;
    });*/
  }

  onKeyboardOpen() {
    this.virtualKeyboardDialog.open({
      keyboard: this.virtualKeyboard,
      initialValue: this.control.value,
      onChange: (input) => {
        this.control.patchValue(input);
      }
    }).then(r => {
      this._virtualKeyboardRef = r;
    })
  }

  ngOnDestroy() {
    if (!this._virtualKeyboardRef) return;

    this._virtualKeyboardRef.close();
  }

  private processAutocompleteValue(value: any): Observable<AutocompleteResult[]> {
    if (!value) {
      return of([]);
    }

    let val = value;
    if (typeof value === 'object') {
      val = Object.keys(value)[0];
    }

    if (this.multipleWordsJoin && this.field && this.field.castorType === CASTOR_FIELD_TYPE_TEXT) {
      const words = this.getWords(val);
      this._lastCursorPosition = this.input['_elementRef']['nativeElement']['selectionStart'];

      const activeWordIndex = this.getActiveWordIndex(words);

      const activeWord = words[activeWordIndex];

      if (activeWord.length === 0) {
        return of([]);
      }

      this.activeWord = activeWord;

      //words.splice(activeWordIndex, 1);

      const wordsWithoutActiveWord = words; /*.filter((_, i) => i !== activeWordIndex)*/

      let query = '';
      wordsWithoutActiveWord.forEach((w) => {
        query += `${this.field.fieldId}:${w.toLowerCase()}* ${this.multipleWordsSeparator} `;
      });
      query = query.substring(0, query.length - this.multipleWordsSeparator.length - 2);

      if (this.query && query) {
        query = `${this.query} ${this.logOperator} ${query}`;
      }

      return this.autocomplete(activeWord, query).pipe(
        map((data) => {
          return data.map((d) => {
            return {
              [[...words.slice(0, activeWordIndex), Object.keys(d)[0], ...words.slice(activeWordIndex + 1)].join(
                ' '
              ) /*+ ' ' + Object.keys(d)[0]]*/]: d[Object.keys(d)[0]]
            };
          });
        }),
        finalize(() => {
          this.isLoading = false;
        })
      );
    }

    if (!this.field || val.length === 0) {
      return of([]);
    }

    return this.autocomplete(val).pipe(
      finalize(() => {
        this.isLoading = false;
      })
    );
  }
}
